// Buffer pH is never a predictor. The coefficient sets are the BpH-dropped refits.

export const TARGET_DEPTH = 8;

/* * Gate ranges, LIVE values from compute (their vendored `predictor_valid_ranges.txt` is stale). */
export const RANGES: Record<string, [number, number]> = {
  CEC: [6.4, 21.5],
  OM: [0.91, 6.89],
  pH: [5.5, 8.1],
  P: [2.6, 101.4],
  K: [29.5, 354.5],
  Ca: [460.0, 3840.0],
  Mg: [133.25, 841.75],
};

const clamp = (p: string, x: number) => {
  const r = RANGES[p];
  return r ? Math.min(Math.max(x, r[0]), r[1]) : x;
};
/*
 *
 * *"Make the change to do it that way so that we can always have the depth readings we need. "*
 * Compute's chain GATES an out-of-range other-nutrient — drops it and swaps to a model refit
 * without it.
 */
const isPresent = (x: number | undefined): x is number =>
  x != null && Number.isFinite(x);

/*
 *
 * Coefficients, keyed by target and by the `incl` set JOINED IN NUTRIENTS ORDER.
 * Each vector is [gap, gap*cec8, gap*om8, gap*own_lin,...gap*incl_i] — the tail in `incl` order.
 */
const COEFFS: Record<string, number[]> = {
  // --- P ---
  "P|pH,K,Ca,Mg": [
    0.361529933, -0.005429953, -0.01020278, -0.000548388, -0.045311158,
    0.000214249, 0.000024875, 0.000180398,
  ],
  "P|pH,K": [
    0.184364827, 0.00021569, -0.002245713, -0.000643872, -0.015350899,
    0.000132313,
  ],
  "P|pH,K,Ca": [
    0.189625542, -0.000141793, -0.002033251, -0.000643507, -0.016176305,
    0.000134746, 0.000002307,
  ],
  "P|pH,K,Mg": [
    0.297659696, -0.00146919, -0.011885412, -0.00055776, -0.035161557,
    0.000184711, 0.000169692,
  ],
  "P|K,Ca,Mg": [
    0.080770287, 0.005624349, -0.007775265, -0.000648751, 0.000097291,
    -0.000046515, 0.000083181,
  ],
  "P|K": [0.087622119, -0.001558562, 0.004098098, -0.000716297, 0.000111063],
  // --- K ---
  "K|pH,P,Ca,Mg": [
    0.467601771, -0.013643807, 0.001814221, 0.000684871, -0.068962257,
    -0.000726928, 0.000087933, 0.000035986,
  ],
  "K|pH,P": [
    0.243058625, 0.000339264, -0.004239667, 0.000581029, -0.033299983,
    -0.000759114,
  ],
  "K|pH,P,Ca": [
    0.433309806, -0.01258891, 0.003443901, 0.000669011, -0.063150356,
    -0.000745902, 0.000083432,
  ],
  "K|pH,P,Mg": [
    0.241816578, 0.000357736, -0.004133987, 0.000580454, -0.0330828,
    -0.000760058, -0.00000186,
  ],
  "K|P,Ca,Mg": [
    0.040293797, 0.003180519, 0.005508828, 0.000506864, -0.000879677,
    -0.000020719, -0.000111975,
  ],
  "K|P": [0.033199225, -0.003509536, 0.009521665, 0.000534931, -0.000916222],
  // --- pH (H+ scale — see `linFuncs`) ---
  "pH|P,K,Ca,Mg": [
    0.08013557, 0.005463437, 0.013532924, -0.000045093, 0.004152968,
    -0.00106942, -0.000189797, 0.00044562,
  ],
  "pH|P,K": [
    0.097475669, -0.020176086, 0.059737031, -0.000025052, 0.003787888,
    -0.000971708,
  ],
};

/* * `NUTRIENTS` order minus the target, minus BpH. POSITIONAL — never sort this. */
const OTHERS: Record<Target, string[]> = {
  P: ["pH", "K", "Ca", "Mg"],
  K: ["pH", "P", "Ca", "Mg"],
  pH: ["P", "K", "Ca", "Mg"],
};

export type Target = "P" | "K" | "pH";

/*
 *
 * The scale each target is modelled on.
 *
 * **pH is fitted on the H+ scale and this is not cosmetic.** Reading compute's pH coefficients on a linear
 * scale lands near 6.99 against a correct 6.45 — half a pH unit, which is a lime decision, and it looks
 * entirely ordinary on screen. Reference vector A is the guard: an implementation missing this transform
 * returns ~7.0.
 */
function linFuncs(target: Target): {
  toLin: (x: number) => number;
  fromLin: (h: number) => number;
} {
  if (target === "pH")
    return {
      toLin: (x) => Math.pow(10, 9 - x),
      fromLin: (h) => 9 - Math.log10(h),
    };
  return { toLin: (x) => x, fromLin: (h) => h };
}

export interface SoilAt8 {
  cec8: number;
  om8: number;
  ph?: number;
  p?: number;
  k?: number;
  ca?: number;
  mg?: number;
}

/* * The CEC/OM chain, exposed for the reference test. The hover path does NOT need it — see `estimateAtDepth`. */
export function cec8Of(depth: number, cecAtDepth: number): number {
  return (
    cecAtDepth *
    Math.exp(
      (depth - TARGET_DEPTH) *
        (0.010352842 - 0.001096897 * clamp("CEC", cecAtDepth)),
    )
  );
}
export function om8Of(depth: number, omAtDepth: number, cec8: number): number {
  return (
    omAtDepth *
    Math.exp(
      (depth - TARGET_DEPTH) *
        (0.027871998 -
          0.004877869 * clamp("OM", omAtDepth) -
          0.000158273 * clamp("CEC", cec8)),
    )
  );
}

/* * Which coefficient vector applies, or null when we don't hold that gated model. */
function coefFor(
  target: Target,
  vals: Record<string, number | undefined>,
): { coef: number[]; incl: string[] } | null {
  const incl = OTHERS[target].filter((p) => isPresent(vals[p]));
  const hit = COEFFS[`${target}|${incl.join(",")}`];
  return hit ? { coef: hit, incl } : null;
}

/*
 *
 * Forward: an at-depth value -> its stored 8" value. Compute's `predict8`, same order, same clamps.
 *
 * The anchor is UNCLAMPED (it tracks the real sample) while the own-value FEATURE is clamped — two
 * different uses of the same number, and collapsing them changes the answer on any out-of-range soil.
 */
function forward8(
  target: Target,
  depth: number,
  cec8: number,
  om8: number,
  vals: Record<string, number | undefined>,
): number | null {
  const c = coefFor(target, vals);
  const own = vals[target];
  if (!c || own == null || !Number.isFinite(own)) return null;
  const { toLin, fromLin } = linFuncs(target);
  const anchor = toLin(own);
  if (!(anchor > 0)) return null;
  const gap = depth - TARGET_DEPTH;
  const feat = [
    gap,
    gap * cec8,
    gap * om8,
    gap * toLin(clamp(target, own)),
    ...c.incl.map((p) => gap * clamp(p, vals[p] as number)),
  ];
  return fromLin(
    anchor * Math.exp(feat.reduce((acc, f, i) => acc + f * c.coef[i], 0)),
  );
}

/*
 *
 * CEC at `depth`, inverted from the stored 8" value.
 *
 * `cec8Of` runs forward and the at-depth CEC appears on both sides (it drives its own clamp term), so this
 * is the same fixed point as P/K — just one-dimensional and with no other nutrient in it. CEC is the chain
 * ROOT: nothing else can be solved without it, which is why it gets its own entry point rather than riding
 * the nutrient solver.
 */
export function cecAtDepth(
  depth: number,
  cec8: number,
  iterations = 12,
): number | null {
  if (!Number.isFinite(cec8) || cec8 <= 0) return null;
  if (depth === TARGET_DEPTH) return cec8;
  let cd = cec8;
  for (let i = 0; i < iterations; i++) {
    const f = cec8Of(depth, cd);
    if (!(f > 0)) return null;
    cd = cd * (cec8 / f);
  }
  return Number.isFinite(cd) && cd > 0 ? cd : null;
}

/*
 *
 * OM at `depth`, inverted from the stored 8" value.
 *
 * Takes the MODELLED 8" CEC directly, exactly as compute's forward chain does — `om8` is solved against
 * the CEC already solved, not against a raw measurement. Without CEC there is no OM estimate, which is the
 * same dependency the ingest chain has rather than a limitation of this implementation.
 */
export function omAtDepth(
  depth: number,
  om8: number,
  cec8: number,
  iterations = 12,
): number | null {
  if (!Number.isFinite(om8) || om8 <= 0 || !Number.isFinite(cec8)) return null;
  if (depth === TARGET_DEPTH) return om8;
  let od = om8;
  for (let i = 0; i < iterations; i++) {
    const f = om8Of(depth, od, cec8);
    if (!(f > 0)) return null;
    od = od * (om8 / f);
  }
  return Number.isFinite(od) && od > 0 ? od : null;
}

export interface DepthEstimate {
  p: number | null;
  k: number | null;
  ph: number | null;
  /* * why a value is null, when it is — for the console, never for the user as a number */
  reason?: string;
  /* * predictors held at their 8" value because we have no target model to solve them */
  heldAt8: string[];
}

/*
 *
 * Because presence cannot change mid-solve, the coefficient set is fixed for the whole iteration.
 */
export function estimateAtDepth(
  depth: number,
  soil: SoilAt8,
  iterations = 12,
): DepthEstimate {
  const heldAt8 = (["ca", "mg"] as const)
    .filter((k) => soil[k] != null)
    .map((k) => k.toUpperCase());
  if (!Number.isFinite(soil.cec8) || !Number.isFinite(soil.om8)) {
    return {
      p: null,
      k: null,
      ph: null,
      reason: "no CEC/OM at this cell — CEC is the chain root",
      heldAt8,
    };
  }
  if (depth === TARGET_DEPTH)
    return {
      p: soil.p ?? null,
      k: soil.k ?? null,
      ph: soil.ph ?? null,
      heldAt8,
    };

  const stored: Record<Target, number | undefined> = {
    P: soil.p,
    K: soil.k,
    pH: soil.ph,
  };
  const cur: Record<Target, number | undefined> = { ...stored };
  if (stored.P == null && stored.K == null && stored.pH == null) {
    return {
      p: null,
      k: null,
      ph: null,
      reason: "no P, K or pH at this cell",
      heldAt8,
    };
  }

  // A target we cannot solve is HELD AT ITS 8" VALUE, not dropped.
  const held = new Set<Target>();
  for (let i = 0; i < iterations; i++) {
    for (const t of ["P", "K", "pH"] as const) {
      const target8 = stored[t];
      if (target8 == null || held.has(t)) continue;
      const vals: Record<string, number | undefined> = {
        ...cur,
        Ca: soil.ca,
        Mg: soil.mg,
      };
      const pred = forward8(t, depth, soil.cec8, soil.om8, vals);
      if (pred == null) {
        held.add(t);
        cur[t] = stored[t];
        continue;
      }
      // Scale in the target's OWN linear space — for pH that is H+, so the update is multiplicative on
      // 10^(9-pH) and not on the pH number itself.
      const { toLin, fromLin } = linFuncs(t);
      const predLin = toLin(pred);
      const wantLin = toLin(target8);
      if (!(predLin > 0) || !(wantLin > 0)) {
        held.add(t);
        cur[t] = stored[t];
        continue;
      }
      const next = fromLin(toLin(cur[t] as number) * (wantLin / predLin));
      if (Number.isFinite(next)) cur[t] = next;
      else {
        held.add(t);
        cur[t] = stored[t];
      }
    }
  }

  // A held target is reported as NULL: it was never estimated, and returning the stored 8" number under a
  // 6" label is exactly the absent-reads-as-a-value mistake this whole readout exists to avoid.
  const ok = (t: Target) => {
    const x = cur[t];
    return !held.has(t) && x != null && Number.isFinite(x) && x > 0 ? x : null;
  };
  for (const t of held) heldAt8.push(t.toUpperCase());
  const out: DepthEstimate = { p: ok("P"), k: ok("K"), ph: ok("pH"), heldAt8 };
  if (out.p == null && out.k == null && out.ph == null) {
    out.reason = "no coefficient set for the in-range predictors at this cell";
  }
  return out;
}

/*
 * Test-only door onto the forward chain, so the reference vectors are checked against the REAL function
 * rather than a re-derivation in the test file.
 */
export function forward8ForTest(
  target: Target,
  depth: number,
  cec8: number,
  om8: number,
  vals: Record<string, number | undefined>,
): number | null {
  return forward8(target, depth, cec8, om8, vals);
}

/* * Compute's published reference vector — the guard against a predictor-order regression. */
export const REFERENCE = {
  depth: 6,
  atDepth: { CEC: 15, OM: 3, P: 30, pH: 6.5, K: 120 },
  cec8: 15.184139518,
  om8: 2.935688473,
  P8: 25.675621,
  /* * pH reference A — incl (P, K). An implementation missing the H+ transform returns ~6.99 instead. */
  pH8_noCaMg: 6.461432995,
  /* * pH reference B — incl (P, K, Ca, Mg), with Ca 2000 / Mg 300 */
  pH8_withCaMg: 6.446939353,
  ca: 2000,
  mg: 300,
};
